
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import { ImageStepperProps } from './query';
import { Svg } from '@/components/styled';
import { List as IconsList } from '@/public/img/icons';
import devices from '@/utilities/devices';
import { renderIcons } from '@/utilities/functions';

// #region STYLE
const StepperElementContainer = styled.div < { isVisible: boolean }>`
	align-items: center;
	align-self: stretch;
	background: ${({ theme }) => theme.color.white};
	box-shadow: ${({ theme }) => `
		0 0 0.625rem rgba(${theme.color.rgbPrune}, 0.1),
		0 0 3.125rem rgba(${theme.color.rgbPrune}, 0.03),
		0 0 4.375rem rgba(${theme.color.rgbPrune}, 0.02)
	`};
	border-radius: 0.25rem;
	display: flex;
	flex: none;
	flex-direction: row;
	flex-grow: 0;
	margin: 2.5rem 0;
	margin-top: 3rem;
	width: 100%;
	transition: 0.3s;

	.icon__container {
		color: ${({ theme }) => theme.color.greyDarker};
		padding: 2rem;
		transition: color 0.3s;
		svg {
			path {
				transition: 0.3s;
			}
		}
	}
	.image__container {
		flex: 1;
		width: 50%;
	}
	.text__container {
		flex: 1;
		.description {
			color: ${({ theme }) => theme.color.greyDarker};
			transition-property: color;
  		transition-duration: 0.3s;
		}
		.title {
			color: ${({ theme }) => theme.color.greyDarker};
			text-transform: uppercase;
			transition-property: color;
  		transition-duration: 0.3s;
		}
	}

 	:hover {
		box-shadow: ${({ theme }) => `
			0 0 0.625rem rgba(${theme.color.rgbPrune}, 0.2),
			0 0 3.125rem rgba(${theme.color.rgbPrune}, 0.06),
			0 0 4.375rem rgba(${theme.color.rgbPrune}, 0.04)
	`};
		transform: translateY(-3px);
		.icon__container {
			color: ${({ theme }) => theme.color.prune};
			svg {
				fill: ${({ theme }) => theme.color.prune};
				circle {
					stroke: ${({ theme }) => theme.color.prune} !important;
				}
				path {
					fill: ${({ theme }) => theme.color.prune} !important;
				}
			}
		}
		.text__container {
			.description {
				color: ${({ theme }) => theme.color.black}
			}
			.title {
				color: ${({ theme }) => theme.color.prune}
			}
		}
	}

	@media screen and (${devices.mobile}) {
		background:  ${({ theme, isVisible }) => isVisible ? theme.color.beige : theme.color.white};
		flex-direction: column;
		.icon__container {
			color: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker};
			svg {
				fill: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker};
				circle {
					stroke: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker} !important;
				}
				path {
					fill: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker} !important;
				}
			}
		}
		.image__container { display: none; }
		.text__container {
			margin: 0 1.875rem 1.875rem;
			text-align: center;
			.description {
				color: ${({ theme, isVisible }) => isVisible ? theme.color.black : theme.color.greyDarker};
			}
			.title {
				color: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker};
			}
		}
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		background:  ${({ theme, isVisible }) => isVisible ? theme.color.beige : theme.color.white};
		flex-direction: column;
		.icon__container {
			color: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker};
			svg {
				fill: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker};
				circle {
					stroke: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker} !important;
				}
				path {
					fill: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker} !important;
				}
			}
		}
		.image__container { display: none; }
		.text__container {
			margin: 0 1.875rem 1.875rem;
			text-align: center;
			.description {
				color: ${({ theme, isVisible }) => isVisible ? theme.color.black : theme.color.greyDarker};
			}
			.title {
				color: ${({ theme, isVisible }) => isVisible ? theme.color.prune : theme.color.greyDarker};
			}
		}
	}
`;
// #endregion

// #region COMPONENT
const ImageStepper = ({ steps }: { steps: ImageStepperProps[] }) => {

	// #region RENDER
	return (
		<>
			{steps.map((step, index) => (
				<InView key={index} threshold={1} triggerOnce rootMargin='-100px 0px'>
					{({ inView, ref }) => (
						<StepperElementContainer
							isVisible={inView}
							ref={ref}
						>
							<div className='icon__container'>
								<Svg
									color='greyDarker'
									size={{
										height: '42px',
										width: '42px',
									}}
								>
									{renderIcons(step.icon.name, IconsList)}
								</Svg>
							</div>
							{index % 2 === 1 &&
								<img
									className='image__container'
									alt={steps[index].imageSource.data.attributes.alternativeText}
									src={steps[index].imageSource.data.attributes.url}
									loading='lazy'
								/>
							}
							<div className='text__container'>
								<div className='title'>{step.title}</div>
								<div className='description'>{step.description}</div>
							</div>
							{index % 2 === 0 &&
								<img
									className='image__container'
									alt={steps[index].imageSource.data.attributes.alternativeText}
									src={steps[index].imageSource.data.attributes.url}
									loading='lazy'
								/>
							}
						</StepperElementContainer>
					)}
				</InView>
			))}
		</>
	);
	// #endregion
};
// #endregion

export default ImageStepper;
